import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useGetSignatureLinkQuery,
  usePortalSendCodeMutation,
} from "../../../slices/PortalSlice";
import {
  PortalAlert,
  PortalAuthFrame,
  portalLink,
  portalPrimary,
} from "../components/PortalUi";
import { errorMessage, firstName } from "../portal.utils";
import {
  getPortalSession,
  rememberEmail,
  rememberFirm,
} from "../portalSession";

/** From the signing email: confirm who it is with an email code, then sign. */
export const PortalSignLink = () => {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const link = useGetSignatureLinkQuery(token, { skip: !token });
  const [sendCode, sendState] = usePortalSendCodeMutation();
  const [error, setError] = useState<string | null>(null);
  const info = link.data;
  const next = info ? `/portal/signatures/${info.requestId}` : "";

  const emailCode = async () => {
    if (!info) return;
    setError(null);
    rememberFirm(info.firmName);
    rememberEmail(info.email);
    const result = await sendCode({ email: info.email });
    if ("error" in result) return setError(errorMessage(result.error));
    navigate(
      `/portal/code?email=${encodeURIComponent(
        info.email,
      )}&next=${encodeURIComponent(next)}`,
    );
  };

  if (link.isLoading)
    return (
      <PortalAuthFrame>
        <span className={"text-sm font-semibold text-[#6B7185]"}>Opening…</span>
      </PortalAuthFrame>
    );

  if (!info)
    return (
      <PortalAuthFrame>
        <h1 className={"m-0 text-2xl font-extrabold"}>
          This link doesn’t work
        </h1>
        <PortalAlert>
          {errorMessage(link.error, "This signing link isn’t valid anymore.")}
        </PortalAlert>
        <Link to={"/portal"} className={portalLink}>
          Go to the client portal
        </Link>
      </PortalAuthFrame>
    );

  return (
    <PortalAuthFrame firmName={info.firmName}>
      <div className={"flex flex-col gap-1.5"}>
        <h1 className={"m-0 text-2xl font-extrabold tracking-[-0.4px]"}>
          Hi {firstName(info.firstName, info.email)}, a document is ready to
          sign
        </h1>
        <span className={"text-sm font-medium leading-relaxed text-[#6B7185]"}>
          <strong className={"text-[#1F2433]"}>{info.documentName}</strong> from{" "}
          {info.firmName}.
        </span>
      </div>
      {getPortalSession() ? (
        <button
          type={"button"}
          className={portalPrimary}
          onClick={() => navigate(next)}>
          Review and sign
        </button>
      ) : (
        <>
          <span
            className={"text-sm font-medium leading-relaxed text-[#3A4256]"}>
            For your security, we’ll email a 6-digit code to{" "}
            <strong>{info.email}</strong> before you sign.
          </span>
          {error && <PortalAlert>{error}</PortalAlert>}
          <button
            type={"button"}
            className={portalPrimary}
            onClick={emailCode}
            disabled={sendState.isLoading}>
            {sendState.isLoading ? "Sending…" : "Email me a code"}
          </button>
        </>
      )}
    </PortalAuthFrame>
  );
};
